import axios from 'axios';
import { normalizeEmail } from '../utils/normalizeEmail';
import { getLoginPath } from '../utils/branding';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
      localStorage.removeItem(tokenKey);
      window.location.href = getLoginPath();
    }
    return Promise.reject(error);
  }
);

export type UserAccessLevel = 'normal' | 'all_access' | 'support' | 'admin';

export interface LoginResponse {
  token: string;
  record: {
    id: string;
    email: string;
    login_id?: string;
    red_rock_id?: string;
    verified: boolean;
    access_level?: UserAccessLevel;
    created: string;
    updated: string;
  };
}

export interface User {
  id: string;
  email: string;
  login_id?: string;
  red_rock_id?: string;
  verified: boolean;
  access_level?: UserAccessLevel;
  created: string;
  updated: string;
}

export interface UserCustomerAssignment {
  id: string;
  client_id: string;
  red_rock_id: string;
  red_rock_full_entity_name: string;
  is_active: boolean;
  user_access_type: 'can_view' | 'can_edit';
  assigned_date: string;
  valid_from?: string;
  valid_to?: string;
  is_current_version: boolean;
  created_at: string;
  updated_at: string;
  /** From expanded red_rock_id (customer_v2) — used for TAL Acquisition branding */
  lead_source?: string | null;
  lead_source_id?: string | { id?: string; name?: string; title?: string; lead_source_id?: string } | null;
}

class AuthService {
  async login(emailOrLoginId: string, password: string): Promise<LoginResponse> {
    const normalizedIdentity = emailOrLoginId.includes('@')
      ? normalizeEmail(emailOrLoginId)
      : emailOrLoginId.trim();

    try {
      console.log('🔐 Attempting login with:', normalizedIdentity);
      
      // Try to authenticate directly
      const response = await api.post('/collections/client_users/auth-with-password', {
        identity: normalizedIdentity,
        password: password,
      });
      
      console.log('✅ Authentication successful');
      return response.data as LoginResponse;
    } catch (error: any) {
      console.error('🚨 Login error:', error);
      console.error('🚨 Error response:', error.response?.data);
      
      if (error.response?.status === 400) {
        // Check if it looks like a login_id (not an email)
        const isEmail = normalizedIdentity.includes('@');
        if (!isEmail) {
          throw new Error('Please use your email address to log in. Login ID authentication is not available.');
        }
        throw new Error('Invalid credentials. Please check your email and password.');
      }
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw new Error('Login failed. Please check your credentials.');
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      // Use POST instead of GET for auth-refresh (PocketBase requirement)
      const response = await api.post('/collections/client_users/auth-refresh');
      
      // Update token if a new one is returned
      if (response.data.token) {
        const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
        localStorage.setItem(tokenKey, response.data.token);
      }
      
      return response.data.record as User;
    } catch (error: any) {
      // Log more details for debugging
      console.error('getCurrentUser error:', error);
      console.error('Error response:', error.response?.data);
      throw new Error('Failed to get current user');
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post('/collections/client_users/auth-logout');
    } catch (error) {
      // Even if logout fails on server, clear local token
      console.error('Logout error:', error);
    }
  }

  async refreshToken(): Promise<string> {
    try {
      const response = await api.post('/collections/client_users/auth-refresh');
      return response.data.token;
    } catch (error) {
      throw new Error('Failed to refresh token');
    }
  }

  async getUserCustomerAssignments(clientId: string, accessLevel?: UserAccessLevel): Promise<{ items: UserCustomerAssignment[]; totalItems: number }> {
    try {
      // If user has all_access or admin, load first 300 customers by default
      if (accessLevel === 'all_access' || accessLevel === 'admin') {
        console.log(`🔓 User has ${accessLevel} - loading first 300 customers`);
        const response = await api.get('/collections/client_customer_access_view/records?perPage=300&page=1&sort=red_rock_full_entity_name');
        const items = response.data.items || [];
        const totalItems = response.data.totalItems || 0;
        
        // Transform view data to match UserCustomerAssignment interface
        const transformedItems: UserCustomerAssignment[] = items.map((item: any) => ({
          id: item.id,
          client_id: typeof item.client_id === 'string' ? item.client_id : (Array.isArray(item.client_id) ? item.client_id[0] : clientId),
          red_rock_id: item.id,
          red_rock_full_entity_name: item.red_rock_full_entity_name || '',
          is_active: item.is_active !== false,
          user_access_type: 'can_view' as const,
          assigned_date: item.assigned_date || new Date().toISOString(),
          is_current_version: item.is_current_version !== false,
          created_at: item.created || new Date().toISOString(),
          updated_at: item.updated || new Date().toISOString(),
        }));
        
        // Deduplicate by red_rock_id (customer ID) - keep first occurrence
        // This handles cases where a customer is assigned to multiple client_users
        const seenRedRockIds = new Set<string>();
        const deduplicatedItems = transformedItems.filter((item) => {
          if (seenRedRockIds.has(item.red_rock_id)) {
            return false; // Skip duplicate
          }
          seenRedRockIds.add(item.red_rock_id);
          return true; // Keep first occurrence
        });
        
        console.log(`🔍 Deduplicated ${transformedItems.length} items to ${deduplicatedItems.length} unique customers`);
        
        return {
          items: deduplicatedItems,
          totalItems: totalItems,
        };
      }
      
      // Normal users: get only assigned customers (expand customer for lead_source branding)
      const filter = `client_id="${clientId}" && is_active=true && is_current_version=true`;
      const response = await api.get(
        `/collections/user_customer_assignment/records?filter=${encodeURIComponent(filter)}&expand=red_rock_id`
      );
      const items = (response.data.items || []).map((item: any) => {
        const customer = item.expand?.red_rock_id;
        return {
          ...item,
          lead_source: customer?.lead_source ?? item.lead_source ?? null,
          lead_source_id: customer?.lead_source_id ?? item.lead_source_id ?? null,
        } as UserCustomerAssignment;
      });
      return {
        items,
        totalItems: items.length,
      };
    } catch (error: any) {
      console.error('Error fetching user customer assignments:', error);
      throw new Error('Failed to fetch user customer assignments');
    }
  }

  /**
   * Fetch lead_source / lead_source_id from customer_v2 for branding detection.
   * Used when assignment expand is unavailable (e.g. all_access view path).
   */
  async getCustomerLeadSourceFields(
    redRockId: string
  ): Promise<{ lead_source: string | null; lead_source_id: string | null }> {
    const pick = (record: any) => ({
      lead_source: record?.lead_source ?? null,
      lead_source_id: record?.lead_source_id ?? null,
    });

    try {
      // Prefer direct fetch by record id (assignments store customer_v2 id in red_rock_id)
      try {
        const byId = await api.get(`/collections/customer_v2/records/${redRockId}`);
        if (byId.data) return pick(byId.data);
      } catch {
        // fall through to filter
      }

      const filter = `id="${redRockId}" || red_rock_id="${redRockId}"`;
      const response = await api.get(
        `/collections/customer_v2/records?filter=${encodeURIComponent(filter)}&perPage=1`
      );
      return pick(response.data.items?.[0]);
    } catch (error) {
      console.error('Failed to fetch customer lead source fields:', error);
      return { lead_source: null, lead_source_id: null };
    }
  }

  async searchCustomers(
    searchTerm: string = '', 
    page: number = 1, 
    perPage: number = 30,
    abortSignal?: AbortSignal
  ): Promise<{ items: UserCustomerAssignment[]; totalItems: number; totalPages: number }> {
    try {
      // Build filter for search
      let filter = '';
      if (searchTerm && searchTerm.trim()) {
        // Search in red_rock_full_entity_name field (case-insensitive contains)
        // Escape quotes in search term
        const escapedTerm = searchTerm.trim().replace(/"/g, '\\"');
        // Use ~ operator for case-insensitive contains search
        const searchFilter = `red_rock_full_entity_name ~ "${escapedTerm}"`;
        filter = searchFilter;
        console.log('🔍 Search filter:', filter);
      }
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        perPage: perPage.toString(),
        sort: 'red_rock_full_entity_name',
        skipTotal: '1', // Skip counting total items for better performance
      });
      
      if (filter) {
        params.append('filter', filter);
      }
      
      const url = `/collections/client_customer_access_view/records?${params.toString()}`;
      console.log('🔍 Search API URL:', url);
      console.log('🔍 Search params:', params.toString());
      
      const response = await api.get(url, {
        signal: abortSignal, // Support request cancellation
      });
      console.log('🔍 Search response:', response.data);
      const items = response.data.items || [];
      
      // Transform view data to match UserCustomerAssignment interface
      const transformedItems: UserCustomerAssignment[] = items.map((item: any) => ({
        id: item.id,
        client_id: typeof item.client_id === 'string' ? item.client_id : (Array.isArray(item.client_id) ? item.client_id[0] : ''),
        red_rock_id: item.id,
        red_rock_full_entity_name: item.red_rock_full_entity_name || '',
        is_active: item.is_active !== false,
        user_access_type: 'can_view' as const,
        assigned_date: item.assigned_date || new Date().toISOString(),
        is_current_version: item.is_current_version !== false,
        created_at: item.created || new Date().toISOString(),
        updated_at: item.updated || new Date().toISOString(),
      }));
      
      // Deduplicate by red_rock_id (customer ID) - keep first occurrence
      // This handles cases where a customer is assigned to multiple client_users
      const seenRedRockIds = new Set<string>();
      const deduplicatedItems = transformedItems.filter((item) => {
        if (seenRedRockIds.has(item.red_rock_id)) {
          return false; // Skip duplicate
        }
        seenRedRockIds.add(item.red_rock_id);
        return true; // Keep first occurrence
      });
      
      console.log(`🔍 Search deduplicated ${transformedItems.length} items to ${deduplicatedItems.length} unique customers`);
      
      return {
        items: deduplicatedItems,
        totalItems: response.data.totalItems || deduplicatedItems.length,
        totalPages: response.data.totalPages || 1,
      };
    } catch (error: any) {
      // Re-throw abort errors as-is
      if (error.name === 'AbortError' || error.name === 'CanceledError' || abortSignal?.aborted) {
        throw error;
      }
      console.error('Error searching customers:', error);
      throw new Error('Failed to search customers');
    }
  }
}

export const authService = new AuthService();
